import React, { useState, useEffect, useCallback, useContext } from 'react'
import {
  Paper, Button, TextField, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Tooltip, Chip, InputAdornment, CircularProgress,
  TablePagination, Select, MenuItem, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import VisibilityIcon from '@mui/icons-material/Visibility'
import RefreshIcon from '@mui/icons-material/Refresh'
import hipersendService from '../../services/hipersend'
import Text from '../Text'
import OutcomeChip from './OutcomeChip'
import { labelReason, labelSource, labelIntent } from './labels'
import { ApiDataWrapperContext } from '../../contexts/ApiDataWrapper'

const sf = hipersendService.smartFilter

// Custo por mensagem cobrado do cliente — fallback quando a instância não tem
// pacote (precoMsgPacote). Espelha a constante da Visão geral do SmartFilter.
const PRICE_PER_MESSAGE = 0.06

const MODE_LABEL = {
  off: 'Desligado', shadow: 'Observação', simulation: 'Simulação', active: 'Ativo'
}

const DECISION_OPTIONS = [
  ['', 'Todas as decisões'], ['ALLOW', 'Liberadas'], ['BLOCK', 'Bloqueadas'],
  ['DELAY', 'Atrasadas'], ['REQUIRE_APPROVAL', 'Aprovação'], ['TRANSFORM', 'Ajustadas'],
  ['TAG_ONLY', 'Marcadas'], ['PASS_THROUGH', 'Passaram']
]
const SOURCE_OPTIONS = [
  ['', 'Todas as origens'], ['ixc', 'IXC'], ['sgp', 'SGP'], ['mkauth', 'MK-Auth'],
  ['atlaz', 'Atlaz'], ['generic', 'Genérico']
]

// Explicações em linguagem simples para cada motivo de decisão. O objetivo é que
// alguém sem conhecimento técnico entenda na hora por que a mensagem foi tratada
// daquele jeito. Motivos sem texto aqui caem no rótulo amigável (labelReason).
const REASON_EXPLANATION = {
  duplicate_within_window: 'Esta mensagem é igual a uma que acabou de ser enviada para o mesmo número. Para não repetir e incomodar o cliente, ela foi bloqueada.',
  daily_recipient_limit: 'Este número já recebeu o máximo de mensagens permitido para um único dia.',
  min_interval: 'Faz pouco tempo desde a última mensagem para este número. O intervalo mínimo entre envios ainda não passou.',
  suppressed_recipient: 'Este número está na sua lista de bloqueio (clientes que pediram para não receber mensagens).',
  budget_cap_reached: 'O teto de envios definido para o período já foi atingido.',
  inadimplencia_antiga: 'Este cliente está com o título em aberto mais antigo há muitos meses — além do limite que você definiu. Para não insistir em quem provavelmente não volta, todas as mensagens a ele são silenciadas (reativação/desbloqueio continuam passando).',
  not_allowed_by_any_rule: 'Na estratégia de lista-branca, só passa o que alguma regra liberar — e nenhuma regra liberou esta mensagem.',
  outside_business_hours: 'A mensagem chegou fora do horário comercial configurado.',
  marketing_outside_hours: 'É uma mensagem de marketing e chegou fora do horário permitido para esse tipo de envio.',
  no_sunday: 'Os envios aos domingos estão desativados nas suas regras.',
  billing_too_early: 'Esta cobrança chegou cedo demais em relação à data de vencimento.',
  amount_too_low: 'O valor desta cobrança está abaixo do mínimo configurado para envio.',
  unresolved_variable: 'A mensagem ainda tem um campo do sistema que não foi preenchido (os trechos destacados abaixo). Enviar assim mandaria um texto quebrado ao cliente.',
  unclassified_message: 'Não foi possível reconhecer o tipo desta mensagem, por isso ela precisou de conferência.',
  high_value_billing: 'É uma cobrança de valor alto, então foi encaminhada para aprovação antes de enviar.'
}

function explanationFor(reason) {
  if (!reason) return ''
  return REASON_EXPLANATION[reason] || labelReason(reason)
}

// Padrões de variável de ERP não substituída: #var#, %var%, {var}.
// SPLIT usa a versão com grupo de captura (mantém os delimitadores no resultado);
// TEST usa uma versão ancorada e sem flag global (evita estado de lastIndex).
const UNRESOLVED_VAR_SPLIT = /(#[a-z0-9_]+#|%[a-z0-9_]+%|\{[a-z0-9_]+\})/gi
const UNRESOLVED_VAR_TEST = /^(#[a-z0-9_]+#|%[a-z0-9_]+%|\{[a-z0-9_]+\})$/i

function fmtDateTime(d) {
  if (!d) return '—'
  const dt = new Date(d)
  return isNaN(dt) ? '—' : dt.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}
function fmtFull(d) {
  if (!d) return '—'
  const dt = new Date(d)
  return isNaN(dt) ? '—' : dt.toLocaleString('pt-BR')
}
function fmtMoney(v) {
  const n = Number(v)
  return isFinite(n) ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : String(v)
}
function dueLabel(n) {
  if (n === null || n === undefined || n === '') return null
  const d = Number(n)
  if (!isFinite(d)) return null
  if (d === 0) return 'vence hoje'
  return d > 0 ? `vence em ${d} dia(s)` : `vencida há ${Math.abs(d)} dia(s)`
}
function ruleSummary(matched) {
  if (!matched || !matched.length) return '—'
  const first = matched[0].ruleName || 'Regra'
  return matched.length > 1 ? `${first} +${matched.length - 1}` : first
}
// Intervalo entre as duas mensagens duplicadas, em linguagem natural.
function intervalLabel(earlier, later) {
  if (!earlier || !later) return ''
  const ms = new Date(later).getTime() - new Date(earlier).getTime()
  if (!isFinite(ms) || ms < 0) return ''
  const min = Math.round(ms / 60000)
  if (min < 1) return 'menos de 1 minuto depois'
  if (min < 60) return `${min} minuto(s) depois`
  const h = Math.round(min / 60)
  return `${h} hora(s) depois`
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3">
      <Text className="text-sm opacity-60">{k}</Text>
      <Text className="text-sm font-medium" style={{ textAlign: 'right' }}>{v}</Text>
    </div>
  )
}

// Mensagem com as variáveis não preenchidas destacadas, para o leigo enxergar
// exatamente qual pedaço ficou "cru".
function HighlightedMessage({ text }) {
  if (!text) return null
  const parts = String(text).split(UNRESOLVED_VAR_SPLIT)
  return (
    <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'ui-monospace, monospace', fontSize: 12.5, lineHeight: 1.5 }}>
      {parts.map((p, i) =>
        UNRESOLVED_VAR_TEST.test(p)
          ? <span key={i} style={{ background: 'rgba(245,158,11,.28)', borderRadius: 4, padding: '0 3px', fontWeight: 600 }}>{p}</span>
          : <span key={i}>{p}</span>
      )}
    </span>
  )
}

// Cartão de uma mensagem (texto cru). Usa Paper outlined para adaptar ao tema.
function MessageCard({ label, when, text, highlight, accent }) {
  return (
    <Paper variant="outlined" className="flex-1" sx={{ borderRadius: '14px', p: 1.75, borderColor: accent || undefined, minWidth: 0 }}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <Text className="text-xs font-semibold uppercase tracking-wide" style={{ color: accent || undefined, opacity: accent ? 1 : 0.6 }}>{label}</Text>
        {when && <Text className="text-xs opacity-50" style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(when)}</Text>}
      </div>
      {text
        ? (highlight
            ? <HighlightedMessage text={text} />
            : <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'ui-monospace, monospace', fontSize: 12.5, lineHeight: 1.5 }}>{text}</span>)
        : <Text className="text-sm opacity-50">Texto original não disponível (mantido por 7 dias).</Text>}
    </Paper>
  )
}

function FactsView({ facts }) {
  if (!facts || typeof facts !== 'object') {
    return <Text className="block text-sm opacity-60">Sem fatos extraídos para esta mensagem.</Text>
  }
  const due = dueLabel(facts.daysToDueDate)
  const dates = Array.isArray(facts.dates) ? facts.dates : []
  const amounts = Array.isArray(facts.amounts) ? facts.amounts : []
  const flags = []
  if (facts.hasPix) flags.push({ label: 'PIX', color: 'info' })
  if (facts.hasBoleto) flags.push({ label: 'Boleto', color: 'info' })
  if (facts.hasUnresolvedVariable) flags.push({ label: 'Variável não preenchida', color: 'error' })

  const rows = []
  if (facts.intent) rows.push(['Intenção', labelIntent(facts.intent)])
  if (due) rows.push(['Vencimento', due])
  if (dates.length) rows.push(['Datas', dates.join(', ')])
  if (amounts.length) rows.push(['Valores', amounts.map(fmtMoney).join(' · ')])
  if (typeof facts.length === 'number') rows.push(['Tamanho', `${facts.length} caracteres`])

  const delq = facts._delinquency
  if (delq && delq.overdueMonths != null) {
    rows.push(['Atraso do cliente', `${delq.overdueMonths} mês(es)`])
    if (delq.oldestOpenDueDate) {
      const od = new Date(delq.oldestOpenDueDate)
      if (!isNaN(od)) rows.push(['Título mais antigo', od.toLocaleDateString('pt-BR')])
    }
    if (delq.openTitleCount) rows.push(['Títulos em aberto (vistos)', String(delq.openTitleCount)])
  }

  if (!rows.length && !flags.length) {
    return <Text className="block text-sm opacity-60">Nenhum fato relevante detectado.</Text>
  }
  return (
    <div className="space-y-1">
      {rows.map(([k, v]) => <Row key={k} k={k} v={v} />)}
      {flags.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-1">
          {flags.map((f) => <Chip key={f.label} size="small" color={f.color} variant="outlined" label={f.label} />)}
        </div>
      )}
    </div>
  )
}

// Bloco didático: explica o motivo e mostra as mensagens envolvidas.
function ComparisonView({ detail, loading, data }) {
  const reason = detail.reason
  const explanation = explanationFor(reason)
  const isBlock = detail.decision === 'BLOCK'
  const current = data && data.currentMessage
  const previous = data && data.previousMessage
  const isDuplicate = reason === 'duplicate_within_window'
  const isUnresolved = reason === 'unresolved_variable' || (current && current.facts && current.facts.hasUnresolvedVariable)

  return (
    <div className="space-y-2">
      {explanation && (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: '14px', p: 1.75,
            borderColor: isBlock ? 'rgba(240,68,56,.4)' : 'rgba(18,183,106,.4)',
            background: isBlock ? 'rgba(240,68,56,.06)' : 'rgba(18,183,106,.06)'
          }}
        >
          <div className="flex items-start gap-2">
            <span style={{ fontSize: '1.1rem', lineHeight: 1.2 }}>{isBlock ? '🚫' : '✅'}</span>
            <Text className="text-sm" style={{ lineHeight: 1.45 }}>{explanation}</Text>
          </div>
        </Paper>
      )}

      {loading ? (
        <div className="flex justify-center py-3"><CircularProgress size={20} /></div>
      ) : isDuplicate && previous ? (
        <div className="flex flex-col sm:flex-row gap-2">
          <MessageCard label="1ª mensagem (enviada)" when={previous.receivedAt} text={previous.rawMessage} accent="#067647" />
          <div className="flex items-center justify-center">
            <Text className="text-xs opacity-50" style={{ whiteSpace: 'nowrap' }}>{intervalLabel(previous.receivedAt, current && current.receivedAt) || '↓'}</Text>
          </div>
          <MessageCard label="Esta mensagem (bloqueada — repetida)" when={current && current.receivedAt} text={current && current.rawMessage} accent="#b42318" />
        </div>
      ) : current || !loading ? (
        <MessageCard
          label="Mensagem avaliada"
          when={current && current.receivedAt}
          text={current && current.rawMessage}
          highlight={isUnresolved}
          accent={isBlock ? '#b42318' : undefined}
        />
      ) : null}
    </div>
  )
}

export default function Decisions({ api, secret, defaultSource }) {
  const { precoMsgPacote } = useContext(ApiDataWrapperContext)
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [decision, setDecision] = useState('')
  const [source, setSource] = useState('')
  const [phone, setPhone] = useState('')
  const [detail, setDetail] = useState(null)
  const [detailData, setDetailData] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)

  // Inclui a origem configurada manualmente nas opções do filtro, quando ela
  // não for um dos códigos já conhecidos.
  const sourceOptions = (() => {
    const ds = (defaultSource || '').trim()
    if (!ds || SOURCE_OPTIONS.some(([v]) => v === ds)) return SOURCE_OPTIONS
    return [...SOURCE_OPTIONS, [ds, labelSource(ds)]]
  })()

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await sf.listDecisions(api, secret, {
        limit: rowsPerPage,
        skip: page * rowsPerPage,
        decision: decision || undefined,
        source: source || undefined,
        phone: phone.replace(/\D/g, '') || undefined
      })
      setRows((r && r.data) || [])
      setTotal((r && r.total) || 0)
    } catch (e) {
      setRows([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [api, secret, rowsPerPage, page, decision, source, phone])

  // Volta pra primeira página quando um filtro muda.
  useEffect(() => { setPage(0) }, [decision, source, phone])
  // Carrega com pequeno debounce (evita disparar a cada tecla no número).
  useEffect(() => {
    const t = setTimeout(load, 300)
    return () => clearTimeout(t)
  }, [load])

  // Busca o detalhe (mensagens) ao abrir o diálogo.
  const openDetail = useCallback(async (d) => {
    setDetail(d)
    setDetailData(null)
    setDetailLoading(true)
    try {
      const data = await sf.getDecisionDetail(api, secret, d._id)
      setDetailData(data || null)
    } catch (e) {
      setDetailData(null)
    } finally {
      setDetailLoading(false)
    }
  }, [api, secret])

  const closeDetail = () => { setDetail(null); setDetailData(null); setDetailLoading(false) }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <FormControl size="small" style={{ minWidth: 170 }}>
          <InputLabel>Decisão</InputLabel>
          <Select label="Decisão" value={decision} onChange={(e) => setDecision(e.target.value)}>
            {DECISION_OPTIONS.map(([v, l]) => <MenuItem key={v || 'all'} value={v}>{l}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" style={{ minWidth: 170 }}>
          <InputLabel>Origem</InputLabel>
          <Select label="Origem" value={source} onChange={(e) => setSource(e.target.value)}>
            {sourceOptions.map(([v, l]) => <MenuItem key={v || 'all'} value={v}>{l}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField
          size="small" placeholder="Filtrar por número" value={phone}
          onChange={(e) => setPhone(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          style={{ width: 200 }}
        />
        <Tooltip title="Atualizar">
          <span><IconButton size="small" onClick={load} disabled={loading}><RefreshIcon fontSize="small" /></IconButton></span>
        </Tooltip>
      </div>

      <Text className="block text-xs opacity-60">
        Nos modos <b>Observação</b> e <b>Simulação</b> as decisões são apenas registradas — nenhuma mensagem é bloqueada de verdade.
      </Text>

      {loading ? (
        <div className="flex justify-center py-6"><CircularProgress size={24} /></div>
      ) : !rows.length ? (
        <Text className="block text-sm opacity-60 py-4">
          Nenhuma decisão registrada com esses filtros. Os registros aparecem conforme as mensagens passam pelo filtro.
        </Text>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '16px' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Quando</TableCell>
                <TableCell>Número</TableCell>
                <TableCell>Decisão</TableCell>
                <TableCell>Motivo</TableCell>
                <TableCell>Origem</TableCell>
                <TableCell>Regra</TableCell>
                <TableCell>Modo</TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((d) => (
                <TableRow key={d._id} hover>
                  <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(d.decidedAt)}</TableCell>
                  <TableCell style={{ fontFamily: 'ui-monospace, monospace' }}>{d.phone || '—'}</TableCell>
                  <TableCell><OutcomeChip type={d.decision} /></TableCell>
                  <TableCell>{labelReason(d.reason) || '—'}</TableCell>
                  <TableCell>{labelSource(d.source)}</TableCell>
                  <TableCell>{ruleSummary(d.matchedRules)}</TableCell>
                  <TableCell><span className="text-xs opacity-70">{MODE_LABEL[d.mode] || d.mode || '—'}</span></TableCell>
                  <TableCell align="right">
                    <Tooltip title="Ver detalhes">
                      <IconButton size="small" onClick={() => openDetail(d)}><VisibilityIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage="Por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </TableContainer>
      )}

      <Dialog open={!!detail} onClose={closeDetail} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '18px' } }}>
        {detail && (
          <>
            <DialogTitle>
              <div className="flex items-center gap-2 flex-wrap">
                <span style={{ fontFamily: 'ui-monospace, monospace' }}>{detail.phone || '—'}</span>
                <OutcomeChip type={detail.decision} />
              </div>
            </DialogTitle>
            <DialogContent dividers>
              <div className="space-y-1 mb-3">
                <Row k="Quando" v={fmtFull(detail.decidedAt)} />
                <Row k="Motivo" v={labelReason(detail.reason) || '—'} />
                <Row k="Origem" v={labelSource(detail.source)} />
                <Row k="Modo" v={MODE_LABEL[detail.mode] || detail.mode || '—'} />
                {typeof detail.durationMs === 'number' && <Row k="Tempo de avaliação" v={`${detail.durationMs} ms`} />}
                {/* Uma decisão que bloqueou poupa 1 mensagem. Mostramos o preço do
                    pacote da instância (precoMsgPacote), não o estimatedSavingsBRL
                    gravado com o custo global default (ex.: R$ 0,04). */}
                {detail.estimatedSavingsBRL > 0 && <Row k="Economia estimada" v={fmtMoney(precoMsgPacote || PRICE_PER_MESSAGE)} />}
              </div>

              <Text className="block text-xs uppercase tracking-wide opacity-60 mb-1">O que aconteceu</Text>
              <ComparisonView detail={detail} loading={detailLoading} data={detailData} />

              <Text className="block text-xs uppercase tracking-wide opacity-60 mt-4 mb-1">Regras que dispararam</Text>
              {(detail.matchedRules && detail.matchedRules.length) ? (
                <div className="flex flex-col gap-1">
                  {detail.matchedRules.map((m, i) => (
                    <div key={i} className="flex items-center justify-between gap-2">
                      <Text className="text-sm">{m.ruleName || 'Regra'}</Text>
                      <OutcomeChip type={m.action} />
                    </div>
                  ))}
                </div>
              ) : <Text className="block text-sm opacity-60">Nenhuma regra deu match (passou direto).</Text>}

              <Text className="block text-xs uppercase tracking-wide opacity-60 mt-4 mb-1">Fatos detectados</Text>
              <FactsView facts={(detailData && detailData.currentMessage && detailData.currentMessage.facts) || detail.facts} />
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDetail}>Fechar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </div>
  )
}
